import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Film, MapPin, Plus, Trash2, Loader2, BadgeCheck, ArrowRight,
  Globe, IdCard, Users, ShoppingCart,
} from "lucide-react";
import OrgTeamPanel from "@/components/OrgTeamPanel";

/**
 * L14 · Exhibitor dashboard.
 *
 * Home for multiplex chains, single-screens, drive-ins, mini-multiplexes.
 * Central widget: the Cinema Halls inventory — every physical property
 * the exhibitor operates. Phase 1 = pure listing + linkage. Phase 2
 * will push showtime notifications back up the value chain to the
 * film's producer + distributor.
 */
export default function ExhibitorDashboard() {
  const [org, setOrg] = useState(null);
  const [halls, setHalls] = useState([]);
  const [loadingHalls, setLoadingHalls] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    api.get("/onboard/me/organization")
      .then(({ data }) => setOrg(data.organization))
      .catch(() => setOrg(null));
    refreshHalls();
  }, []);

  const refreshHalls = () => {
    setLoadingHalls(true);
    api.get("/exhibitor/halls/mine")
      .then(({ data }) => setHalls(data.halls || []))
      .catch(() => setHalls([]))
      .finally(() => setLoadingHalls(false));
  };

  if (!org) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55"
           data-testid="exhibitor-loading">Loading dashboard…</div>
    );
  }

  const accent = "#DC2626";
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="exhibitor-dashboard">

      {/* HERO */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        L14 · Exhibitor
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
          style={{ color: accent }}
          data-testid="exhibitor-brand">
        {org.brand_name || org.legal_name}
      </h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        {org.legal_name && org.legal_name !== org.brand_name && (
          <span>{org.legal_name}</span>
        )}
        {org.verified ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300"
                data-testid="exhibitor-verified-chip">
            <BadgeCheck size={10} className="inline mr-1"/> Verified
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-amber-500/15 text-amber-300"
                data-testid="exhibitor-unverified-chip">KYC Pending</span>
        )}
        {org.registered_city && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
            <MapPin size={10} className="inline mr-1"/> {org.registered_city}
          </span>
        )}
      </div>
      <p className="mt-4 text-sm text-white/60 max-w-2xl leading-relaxed">
        Manage the cinema properties you operate. Producers and distributors
        can now discover your inventory when planning a release.
      </p>

      {/* CINEMA HALLS INVENTORY */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">Cinema Halls</div>
            <div className="font-display text-lg uppercase tracking-tight mt-1"
                 data-testid="exhibitor-halls-heading">
              Your Properties {halls.length > 0 && <span className="text-white/40">· {halls.length}</span>}
            </div>
          </div>
          <Button className="h-10 rounded-full text-[11px] uppercase tracking-[0.22em] bg-red-500 text-white hover:bg-red-400"
                  onClick={() => setShowAdd(true)}
                  data-testid="exhibitor-add-hall-btn">
            <Plus size={13} className="mr-1.5"/> Add Hall
          </Button>
        </div>

        {loadingHalls ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/50" data-testid="exhibitor-halls-loading">
            <Loader2 size={16} className="animate-spin inline mr-2"/> Loading properties…
          </div>
        ) : halls.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center" data-testid="exhibitor-halls-empty">
            <Film size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No cinema halls linked yet.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              Add every property you operate so producers, distributors and
              location scouts can find you.
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exhibitor-halls-list">
            {halls.map((h) => (
              <HallCard key={h.id} hall={h} onDelete={refreshHalls}/>
            ))}
          </div>
        )}
      </div>

      {/* TILE GRID — other desks */}
      <div className="mt-12">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-3">Your Desks</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exhibitor-tiles">
          <Tile to="/slate/discovery" TileIcon={Film}
                title="Films Available"
                desc="Every film every distributor has publicly listed — book what's licensable."
                accent={accent}/>
          <Tile to="/directory/orgs?type=distributor" TileIcon={Users}
                title="Distributor Directory"
                desc="Every verified distributor — theatrical, OTT, TV, overseas."
                accent={accent}/>
          <Tile to="/directory/orgs?type=production_house" TileIcon={ShoppingCart}
                title="Production Houses"
                desc="Producers behind upcoming films — pitch your inventory early."
                accent={accent}/>
          <Tile to="/office/public-profile" TileIcon={Globe}
                title="Public Profile"
                desc="Your exhibitor page — brands and producers land here."
                accent={accent}/>
          <Tile to="/icard" TileIcon={IdCard}
                title="Owner iCard"
                desc="Your verified, co-branded identity card."
                accent={accent}/>
        </div>
      </div>

      {/* TEAM PANEL */}
      <div className="mt-10">
        <OrgTeamPanel org={org}/>
      </div>

      {/* ADD HALL MODAL */}
      {showAdd && (
        <AddHallModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refreshHalls(); }}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
function HallCard({ hall, onDelete }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!confirm(`Remove ${hall.name} from your inventory?`)) return;
    setBusy(true);
    try {
      await api.delete(`/exhibitor/halls/${hall.id}`);
      toast.success("Hall removed.");
      onDelete();
    } catch {
      toast.error("Could not remove hall.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cs-card rounded-xl p-5" data-testid={`exhibitor-hall-${hall.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/15 text-red-300">
          <Film size={18}/>
        </div>
        <button onClick={remove} disabled={busy}
                className="text-white/40 hover:text-red-400 transition p-1 rounded-full"
                data-testid={`exhibitor-hall-delete-${hall.id}`}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
        </button>
      </div>
      <div className="mt-3">
        <div className="font-display text-base uppercase tracking-tight">{hall.name}</div>
        <div className="text-[11px] text-white/60 mt-1">
          <MapPin size={9} className="inline mr-1"/>
          {hall.city}{hall.state ? `, ${hall.state}` : ""}
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/8 grid grid-cols-2 gap-3 text-[11px] text-white/70">
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Type</div>
          <div className="mt-0.5">{prettyHallType(hall.hall_type)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Seats · Screens</div>
          <div className="mt-0.5">{hall.seats || "?"} · {hall.screens || 1}</div>
        </div>
      </div>
      {(hall.sound_formats?.length > 0 || hall.projection_formats?.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1">
          {hall.projection_formats?.slice(0, 2).map((f) => (
            <span key={f} className="px-1.5 py-0.5 rounded-full bg-white/8 text-[9px] tracking-[0.14em] uppercase text-white/70">{f}</span>
          ))}
          {hall.sound_formats?.slice(0, 2).map((f) => (
            <span key={f} className="px-1.5 py-0.5 rounded-full bg-white/8 text-[9px] tracking-[0.14em] uppercase text-white/70">{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function prettyHallType(t) {
  return {
    multiplex_screen: "Multiplex Screen",
    single_screen:    "Single Screen",
    drive_in:         "Drive-In",
    indie_micro:      "Indie / Micro",
  }[t] || t;
}

function Tile({ to, TileIcon, title, desc, accent }) {
  return (
    <Link to={to} className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block group"
          data-testid={`exhibitor-tile-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
           style={{ background: `${accent}22`, color: accent }}>
        <TileIcon size={18}/>
      </div>
      <div className="mt-4">
        <div className="font-display text-lg uppercase tracking-tight">{title}</div>
        <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">{desc}</p>
      </div>
      <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
          Open →
        </span>
        <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition"/>
      </div>
    </Link>
  );
}

/* ────────────────────────────────────────────────────────────── */
function AddHallModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", hall_type: "single_screen", city: "", state: "",
    address: "", screens: 1, seats: 0,
    sound_formats: [], projection_formats: [],
  });
  const [meta, setMeta] = useState({ sound_formats: [], projection_formats: [], hall_types: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/exhibitor/meta").then(({ data }) => setMeta(data));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k, v) => setForm((f) => ({
    ...f,
    [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  const submit = async () => {
    if (form.name.trim().length < 2 || form.city.trim().length < 1) {
      toast.error("Name and city are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/exhibitor/halls", {
        ...form,
        screens: Number(form.screens) || 1,
        seats: Number(form.seats) || 0,
      });
      toast.success("Hall added.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add hall.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="exhibitor-add-hall-modal">
      <div className="w-full max-w-2xl cs-card rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/15 text-red-300 flex items-center justify-center">
            <Film size={18}/>
          </div>
          <div>
            <div className="font-display text-lg uppercase tracking-tight">Add Cinema Hall</div>
            <div className="text-[11px] text-white/55">Every property you operate — link them all.</div>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Field label="Hall Name *">
            <Input value={form.name} onChange={set("name")}
                   placeholder="e.g. PVR Phoenix MarketCity"
                   className="bg-black/40 border-white/15"
                   data-testid="exhibitor-hall-name"/>
          </Field>
          <Field label="Hall Type *">
            <select value={form.hall_type} onChange={set("hall_type")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="exhibitor-hall-type">
              {(meta.hall_types || []).map((t) => (
                <option key={t} value={t}>{prettyHallType(t)}</option>
              ))}
            </select>
          </Field>
          <Field label="City *">
            <Input value={form.city} onChange={set("city")}
                   placeholder="e.g. Mumbai"
                   className="bg-black/40 border-white/15"
                   data-testid="exhibitor-hall-city"/>
          </Field>
          <Field label="State">
            <Input value={form.state} onChange={set("state")}
                   placeholder="e.g. MH" maxLength={6}
                   className="bg-black/40 border-white/15"
                   data-testid="exhibitor-hall-state"/>
          </Field>
          <Field label="Screens">
            <Input value={form.screens} onChange={set("screens")}
                   type="number" min={1}
                   className="bg-black/40 border-white/15"
                   data-testid="exhibitor-hall-screens"/>
          </Field>
          <Field label="Total Seats">
            <Input value={form.seats} onChange={set("seats")}
                   type="number" min={0}
                   className="bg-black/40 border-white/15"
                   data-testid="exhibitor-hall-seats"/>
          </Field>
        </div>

        <div className="mt-5">
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Sound Formats</Label>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="exhibitor-hall-sound-formats">
            {meta.sound_formats.map((f) => (
              <button key={f} type="button" onClick={() => toggle("sound_formats", f)}
                      className={`px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase transition ${
                        form.sound_formats.includes(f)
                          ? "bg-red-500 text-white"
                          : "bg-white/8 text-white/60 hover:bg-white/15"
                      }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Projection Formats</Label>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="exhibitor-hall-projection-formats">
            {meta.projection_formats.map((f) => (
              <button key={f} type="button" onClick={() => toggle("projection_formats", f)}
                      className={`px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase transition ${
                        form.projection_formats.includes(f)
                          ? "bg-red-500 text-white"
                          : "bg-white/8 text-white/60 hover:bg-white/15"
                      }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost"
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5"
                  onClick={onClose}
                  data-testid="exhibitor-hall-cancel">Cancel</Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-red-500 text-white hover:bg-red-400 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="exhibitor-hall-save">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Saving…</> : <>Save Hall</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
